import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type ActiveScopeEntry,
	type DismissCallback,
	type PostOfficeContextValue,
	type RouteMatch,
	type RouteState,
	createMatchScopes,
	useContextScope,
	useMessagePlacementHooks,
} from '@atlassian/post-office-context';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';
import type { ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { usePlacementApi } from '@post-office/use-placement-api';
import { type ComponentType, memo, useCallback, useEffect, useState } from 'react';

import { EXPERIMENTAL_MESSAGES } from './experimental-messages-list';
import { ScreenSpaceFlagsPlacementNewDismissals } from './placement-new-dismissals';
import { scopes } from './scopes';
import { SuppressIfModalIsPresent } from './suppress-if-modal-is-present';
import { useEpMigrationTriggers } from './use-ep-migration-triggers';
import { MessageRenderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withScreenSpaceFlagsIntlProvider } from '../__generated__/intl-provider';
import { type ScreenSpaceFlagsMessageState } from '../api/types';
import type { Placement as ScreenSpaceFlagsPlacement } from '../contracts/Placement';
import { usePostOfficeSearchParams } from './use-post-office-search-params';

export const matchedScopes = (input: {
	route: RouteState | RouteMatch;
	context: PostOfficeContextValue;
}): ActiveScopeEntry[] => createMatchScopes({ scopes })(input);

export const ScreenSpaceFlagsPlacementComponent: ComponentType<
	ErrorBoundariedProps<ScreenSpaceFlagsPlacement>
> = withScreenSpaceFlagsIntlProvider(
	createPlacementComponent<ScreenSpaceFlagsPlacement>(
		PLACEMENT_ID,
		memo((props) => {
			const newDismissalLogicIsEnabled = FeatureGates.checkGate(
				'post-office_ssf-enable-new-dismissal-logic',
			);

			if (newDismissalLogicIsEnabled) {
				return <ScreenSpaceFlagsPlacementNewDismissals {...props} />;
			} else {
				return <ScreenSpaceFlagsPlacementOriginalDismissals {...props} />;
			}
		}),
		{
			choreographer: {
				isDefaultEnabled: true, // this is the default value, but leaving it as explicitly defined
			},
		},
	),
);

const ScreenSpaceFlagsPlacementOriginalDismissals = ({
	initialData,
	preloadedInitialData,
	setPreloadedInitialData,
	jiraFlagsService,
	reportExpectedError,
	reportUnexpectedError,
}: ScreenSpaceFlagsPlacement & {
	initialData: Promise<Response | undefined> | undefined;
}) => {
	const isEPExternalMessageSSFEnabled = FeatureGates.checkGate(
		'post-office_enable_ep_external_message_web',
	);

	const isEPExternalMessageSSREnabled = FeatureGates.checkGate(
		'post-office_enabled_ep_external_message_ssr',
	);

	const [localMessageState, setLocalMessageState] = useState<
		ScreenSpaceFlagsMessageState[] | undefined
	>(() => {
		if (preloadedInitialData) {
			return preloadedInitialData?.messages?.length > 0
				? preloadedInitialData?.messages
				: undefined;
		}

		return undefined;
	});

	const [localExternalMessageState, setLocalExternalMessageState] = useState<
		ScreenSpaceFlagsMessageState[] | undefined
	>(() => {
		// Initialize from preloadedInitialData only if both feature gates are enabled
		if (
			isEPExternalMessageSSREnabled &&
			isEPExternalMessageSSFEnabled &&
			preloadedInitialData?.externalMessages
		) {
			return preloadedInitialData.externalMessages.length > 0
				? preloadedInitialData.externalMessages
				: undefined;
		}
		return undefined;
	});
	useInitialData(initialData);

	const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

	const handleDismiss = useCallback<DismissCallback>(
		({ messageInstanceId }: { messageInstanceId: string }) => {
			if (isEPExternalMessageSSREnabled && isEPExternalMessageSSFEnabled) {
				// NEW LOGIC: Improved dismissal handling with precise checking and state tracking
				let updatedLocalMessageState: ScreenSpaceFlagsMessageState[] | undefined;

				// Check if message is in localMessageState
				setLocalMessageState((current) => {
					if (current?.some((msg) => msg.messageInstanceId === messageInstanceId)) {
						const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);
						updatedLocalMessageState = newState;

						if (typeof setPreloadedInitialData === 'function') {
							setPreloadedInitialData({
								messages: newState,
								externalMessages: preloadedInitialData?.externalMessages,
							});
						}

						return newState;
					}

					return current;
				});

				// Check if message is in localExternalMessageState
				setLocalExternalMessageState((current) => {
					if (current?.some((msg) => msg.messageInstanceId === messageInstanceId)) {
						const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);
						const updatedExternalMessages = newState.length > 0 ? newState : undefined;

						if (typeof setPreloadedInitialData === 'function') {
							setPreloadedInitialData({
								messages: updatedLocalMessageState ?? preloadedInitialData?.messages ?? [],
								externalMessages: updatedExternalMessages,
							});
						}

						return updatedExternalMessages;
					}

					return current;
				});
			} else {
				// OLD LOGIC: Original behavior when feature gate is disabled
				setLocalMessageState((current) => {
					if (current) {
						const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);

						if (typeof setPreloadedInitialData === 'function') {
							setPreloadedInitialData({
								messages: newState,
							});
						}

						return newState;
					}

					return undefined;
				});
			}
		},
		[
			setPreloadedInitialData,
			preloadedInitialData,
			isEPExternalMessageSSFEnabled,
			isEPExternalMessageSSREnabled,
		],
	);

	const onDismissed = useCallback(
		(_messageInstanceId: string) => {
			setLocalMessageState([]);

			if (isEPExternalMessageSSREnabled && isEPExternalMessageSSFEnabled) {
				// NEW LOGIC: Clear external message state and update preloadedInitialData
				setLocalExternalMessageState(undefined);

				if (typeof setPreloadedInitialData === 'function') {
					setPreloadedInitialData({
						messages: [],
						...(isEPExternalMessageSSFEnabled && {
							externalMessages: undefined,
						}),
					});
				}
			} else {
				// OLD LOGIC: Only clear regular messages
				if (typeof setPreloadedInitialData === 'function') {
					setPreloadedInitialData({
						messages: [],
					});
				}
			}
		},
		[setPreloadedInitialData, isEPExternalMessageSSFEnabled, isEPExternalMessageSSREnabled],
	);

	useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
	useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
	useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: true,
	});

	const searchParams = usePostOfficeSearchParams();
	const messagesState = usePlacementApi<{
		messages: ScreenSpaceFlagsMessageState[];
		externalMessages?: ScreenSpaceFlagsMessageState[];
	}>('/update-messages', {
		queryParams: {
			...searchParams,
			updateKey: activeScopes.updateKey ?? '',
		},
		requestOptions: { method: 'POST', body: JSON.stringify(activeScopes) },
		throttleTime: 100,
		autoRefetch: {
			isEnabled: FeatureGates.checkGate('post-office-ssf-enable-continuous-auto-refetch')
				? !!activeScopes.updateKey && !localMessageState?.length
				: !!activeScopes.updateKey && typeof localMessageState === 'undefined',
			updateKey: activeScopes.updateKey ?? '',
		},
	});

	useEffect(() => {
		if (messagesState?.data?.messages?.length) {
			const messages = messagesState.data.messages;
			let selectedMessage: ScreenSpaceFlagsMessageState | undefined;

			// New experiments in the screen-space-flags placement must be added to the EXPERIMENTAL_MESSAGES list
			for (const message of messages) {
				const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
					(p) => p.messageTemplateId === message.messageTemplateId,
				);

				if (experimentalMessage) {
					const cohort = FeatureGates.getExperimentValue<string>(
						experimentalMessage.statsigExperiment,
						experimentalMessage.statsigParameterName,
						'not-enrolled',
						{ fireExperimentExposure: true },
					);

					if (experimentalMessage?.statsigAAExperiment) {
						FeatureGates.getExperimentValue<string>(
							experimentalMessage.statsigAAExperiment,
							experimentalMessage.statsigParameterName,
							'not-enrolled',
							{ fireExperimentExposure: true },
						);
					}

					if (cohort === experimentalMessage.statsigTestCohort) {
						selectedMessage = message;
						break;
					}
				} else {
					selectedMessage = message;
					break;
				}
			}
			setLocalMessageState(selectedMessage ? [selectedMessage] : []);
		}

		if (isEPExternalMessageSSFEnabled && messagesState.data?.externalMessages?.length) {
			setLocalExternalMessageState(messagesState.data.externalMessages);
		}
	}, [messagesState.data]);

	const { triggerStates: epTriggerStates, EpTriggerWrapper } = useEpMigrationTriggers(
		isEPExternalMessageSSFEnabled ? localExternalMessageState : undefined,
	);

	// find if an EP message has been triggered
	const activeEpMessageInstanceId = isEPExternalMessageSSFEnabled
		? Object.keys(epTriggerStates).find(
				(messageInstanceId) => epTriggerStates[messageInstanceId] === true,
			)
		: undefined;

	// Check for EP External messages first because they are not rendered until triggered from the front-end
	// and they will not be rendered if a non-ep message is currently rendered due to the choreographer
	const [currentLocalMessage] = localMessageState ?? [];

	const currentExternalMessage = localExternalMessageState?.find(
		(msg) => msg.messageInstanceId === activeEpMessageInstanceId,
	);

	const activeExternalMessage =
		isEPExternalMessageSSFEnabled && activeEpMessageInstanceId
			? currentExternalMessage
			: currentLocalMessage;

	const messageToRender = currentLocalMessage ?? activeExternalMessage;

	const content = FeatureGates.checkGate('post-office-screen-space-flags-shadow') ? (
		<SuppressIfModalIsPresent>
			{messageToRender ? (
				<MessageRenderer
					{...messageToRender}
					key={messageToRender.messageInstanceId}
					id={messageToRender.messageInstanceId}
					messageInstanceId={messageToRender.messageInstanceId}
					onDismissed={onDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
					jiraFlagsService={jiraFlagsService}
					onError={reportUnexpectedError}
					reportExpectedError={reportExpectedError}
				/>
			) : null}
		</SuppressIfModalIsPresent>
	) : null;

	// Wrap content with trigger tracker components if feature gate is enabled
	return isEPExternalMessageSSFEnabled ? <EpTriggerWrapper>{content}</EpTriggerWrapper> : content;
};

export default ScreenSpaceFlagsPlacementComponent;
